import { useCallback, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { googleOAuth, login } from "../api";
import type {
  ActionMessages,
  BusinessOption,
  LoginApiResponse,
  LoginErrorResponse,
} from "../types/login.types";
import { isBusinessSelectionResponse } from "../types/login.types";
import { showNotification } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import { setLastBusinessLogin } from "@/utils/cookieUtils";
import { safeLoginRedirectPath } from "../utils/redirect";
import {
  listRequiredActions,
  redirectForRequiredActions,
} from "../utils/requiredActionRouting";

export interface GoogleCredential {
  idToken: string;
  nonce?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
  businessSlug?: string;
  skipRedirect?: boolean;
}

interface UseLoginUserOptions {
  redirectPath?: string | null;
}

function hasRequiredActions(data: LoginApiResponse): boolean {
  if (isBusinessSelectionResponse(data)) {
    return false;
  }
  return Object.values(data.content.requiresAction || {}).some(Boolean);
}

export const useLoginUser = (options: UseLoginUserOptions = {}) => {
  const { businessSlug } = useParams();
  const navigate = useNavigate();
  const redirectPath = safeLoginRedirectPath(options.redirectPath ?? null);
  const [actions, setActions] = useState<string[]>([]);
  const [actionMessages, setActionMessages] = useState<ActionMessages | null>(
    null
  );
  const [businessChoices, setBusinessChoices] = useState<
    BusinessOption[] | null
  >(null);

  // Track the last email/password so selectBusiness can re-submit
  const lastCredentials = useRef<{ email: string; password: string }>({
    email: "",
    password: "",
  });

  // When Google OAuth returns business picker, store credential for re-submit with selected slug
  const pendingGoogleCredential = useRef<GoogleCredential | null>(null);

  const mutation = useMutation<
    LoginApiResponse,
    LoginErrorResponse,
    LoginCredentials
  >({
    mutationFn: (credentials) => {
      const loginData = { ...credentials };
      if (businessSlug) {
        loginData.businessSlug = businessSlug;
      }
      if (redirectPath) {
        loginData.skipRedirect = true;
      }
      // Save credentials for potential business selection re-submit
      lastCredentials.current = {
        email: credentials.email,
        password: credentials.password,
      };
      return login(loginData);
    },

    onSuccess: (data) => {
      // Backend is asking the user to pick a business
      if (isBusinessSelectionResponse(data)) {
        setBusinessChoices(data.content.businesses);
        return;
      }

      // Clear any previous business choices on successful login
      setBusinessChoices(null);

      const requiredActions = listRequiredActions(data.content.requiresAction);

      setActions(requiredActions);
      setActionMessages(data.content.message as ActionMessages);

      if (redirectForRequiredActions(data.content.requiresAction)) {
        return;
      }

      // Store last business login for convenience (only if login successful and no actions required)
      if (
        requiredActions.length === 0 &&
        businessSlug &&
        data.content.user?.businessName
      ) {
        try {
          setLastBusinessLogin(businessSlug, data.content.user.businessName);
          console.log("Stored last business login:", {
            slug: businessSlug,
            name: data.content.user.businessName,
          });
        } catch (error) {
          console.error("Error setting last business login cookie:", error);
        }
      }
      if (requiredActions.length === 0 && redirectPath) {
        window.location.assign(redirectPath);
      }
    },

    onError: (error) => {
      switch (error.content.error) {
        case "credentials_required":
          showNotification({
            title: "Missing Information",
            message: error.content.details,
            color: "red",
            autoClose: 10000,
          });
          break;

        case "invalid_credentials":
          showNotification({
            title: "Login Failed",
            message: error.content.details,
            color: "red",
            autoClose: 10000,
          });
          break;

        case "user_archived":
          showNotification({
            title: "Account Archived",
            message: error.content.details,
            color: "orange",
            autoClose: 10000,
          });
          break;

        case "emailNotVerified":
          showNotification({
            title: "Email Not Verified",
            message: error.content.details,
            color: "orange",
            autoClose: 10000,
          });
          break;

        case "business_access_denied":
          showNotification({
            title: "Business Access Denied",
            message: error.content.details,
            color: "red",
            autoClose: 10000,
          });
          if (error.content.url) {
            const path = error.content.url.startsWith("/")
              ? error.content.url
              : `/${error.content.url}`;
            navigate(path, { replace: true });
          }
          break;

        case "not_authorized":
          showNotification({
            title: "Redirecting to your Tax Pro's login page",
            message:
              "Please wait while we redirect you to your Tax Pro's login page",
            color: "black",
            autoClose: 3000,
            loading: true,
          });
          // wait for 2 seconds before navigating
          setTimeout(() => {
            if (error.content.error === "not_authorized" && error.content.url) {
              const path = error.content.url.startsWith("/")
                ? error.content.url
                : `/${error.content.url}`;
              navigate(path, { replace: true });
            }
            showNotification({
              title: "You are ready to login",
              message: "Please use your credentials to access your portal",
              color: "green",
              autoClose: 5000,
            });
          }, 2000);
          break;

        default:
          break;
      }
    },
  });

  const setGoogleBusinessPicker = useCallback(
    (businesses: BusinessOption[], credential: GoogleCredential) => {
      setBusinessChoices(businesses);
      pendingGoogleCredential.current = credential;
    },
    []
  );

  const selectBusiness = useCallback(
    async (slug: string) => {
      const cred = pendingGoogleCredential.current;
      if (cred) {
        pendingGoogleCredential.current = null;
        setBusinessChoices(null);
        try {
          const result = await googleOAuth({
            idToken: cred.idToken,
            nonce: cred.nonce,
            businessSlug: slug,
            skipRedirect: Boolean(redirectPath),
          });
          if (isBusinessSelectionResponse(result)) {
            return;
          }
          if (redirectPath && !hasRequiredActions(result)) {
            window.location.assign(redirectPath);
            return;
          }
          redirectForRequiredActions(result.content.requiresAction);
        } catch (error: unknown) {
          const err = error as Partial<LoginErrorResponse> & {
            content?: {
              error?: string;
              details?: string;
              url?: string;
            };
            message?: string;
          };
          showNotification({
            title: "Sign-in failed",
            message:
              err?.content?.details ||
              err?.message ||
              "Failed to sign in for the selected business. Please try again.",
            color: "red",
          });

          if (
            (err?.content?.error === "business_access_denied" ||
              err?.content?.error === "not_authorized") &&
            err?.content?.url
          ) {
            const path = err.content.url.startsWith("/")
              ? err.content.url
              : `/${err.content.url}`;
            navigate(path, { replace: true });
          }
        }
        return;
      }
      setBusinessChoices(null);
      mutation.mutate({
        email: lastCredentials.current.email,
        password: lastCredentials.current.password,
        businessSlug: slug,
      });
    },
    [mutation, redirectPath]
  );

  return {
    ...mutation,
    actions,
    actionMessages,
    businessSlug,
    businessChoices,
    selectBusiness,
    setGoogleBusinessPicker,
  };
};
